import React, { useRef, useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
// Box migrated to UDS
import { Button, Spinner, Typography, Divider, Box, StackView, Notification } from '@telus-uds/components-web';

import Download from '@telus-uds/palette-allium/build/web/icons/Download';

import request from '../../utils/request';
import { ErrorMessageWrapper } from '../css';

const BrpFileUpload = ({ data }) => {
  const { name, required, label, onChange, formErrors, value } = data;
  const [selectedFiles, setSelectedFiles] = useState();
  const [showError, setShowError] = useState({ isVisible: false, message: '' });
  const [isUploading, setIsUploading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const inputRef = useRef();
  const fieldError = _.get(formErrors, `${name}.text`, false);
  let confirmUpload = false;

  const onFileSelect = e => {
    const {
      target: { files }
    } = e;

    setShowError({ isVisible: false, message: '' });
    setSelectedFiles(files);
  };

  const uploadFile = () => {
    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i += 1) {
      formData.append('files', selectedFiles[i]);
    }
    return request.post(`/brp/fileupload`, formData);
  };

  const onFileUpload = async () => {
    if (value && !confirmUpload) {
      setShowWarning(true);
      return;
    }
    if (value && confirmUpload) setShowWarning(false);

    if (!selectedFiles) {
      setShowError({
        isVisible: true,
        message: 'Please select the file'
      });
      return;
    }
    setIsUploading(true);
    try {
      const result = await uploadFile();
      if (result) {
        onChange({
          target: {
            name,
            value: _.get(result, 'data.data', []),
            checked: false
          }
        });
        setIsUploading(false);
        setSelectedFiles(null);
        inputRef.current.value = '';
      }
    } catch (error) {
      setShowError({
        isVisible: true,
        message: 'There was an error uploading the file'
      });
      setIsUploading(false);
      throw error;
    }
  };

  const onFileDownload = url => {
    window.open(url, '_blank');
  };

  const confirmFileUpload = () => {
    confirmUpload = true;
    onFileUpload();
  };

  return (
    <StackView space={2}>
      <div>
        <Typography>
          {label}
          {required && `*`}
        </Typography>
      </div>
      <Box>
        <input
          type="file"
          name={name}
          onChange={onFileSelect}
          accept="application/msword, application/pdf, image/jpeg, image/png"
          ref={inputRef}
          multiple
        />
      </Box>
      <Box>
        {!showWarning && (
          <Spinner
            label="Uploading file..."
            variant={{ size: 'small' }}
            show={isUploading}
            inline
          >
            <Button onClick={onFileUpload}>Upload</Button>
          </Spinner>
        )}
        {showWarning && (
          <StackView space={2}>
            <Notification variant={{ validation: 'warning' }} copy="en">
              Warning: Previous attachments will be removed, do you want to
              continue?
            </Notification>
            <StackView direction={{ xs: 'column', lg: 'row' }} space={{ xs: 1, md: 1, xl: 2 }}>
              <Button onClick={confirmFileUpload}>Yes</Button>
              <Button variant={{ priority: 'low' }} onClick={() => setShowWarning(false)}>
                No
              </Button>
            </StackView>
          </StackView>
        )}

        {showError.isVisible && (
          <ErrorMessageWrapper>{showError.message}</ErrorMessageWrapper>
        )}
        {!showError.isVisible && !value && fieldError && (
          <ErrorMessageWrapper>{fieldError}</ErrorMessageWrapper>
        )}
      </Box>
      {value && (
        <>
          <Divider space={2} variant={{ inverse: true }}/>
          <StackView space={3}>
            <Typography heading="h4" variant={{ size: 'h4' }} block>Uploaded attachments</Typography>
            <ul className="list-group list-group-flush">
              {value &&
                value.map(imgaeUrl => (
                  <li>
                    <div
                      className="card"
                      style={{
                        margin: '0.5rem 0',
                        display: 'flex',
                        flexDirection: 'row',
                        padding: '1rem 3rem',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography block>{imgaeUrl}</Typography>
                      <Button
                        icon={Download}
                        onPress={() => onFileDownload(imgaeUrl)}
                        accessibilityLabel="Download"
                        variant={{ text: true }}
                      />
                    </div>
                  </li>
                ))}
            </ul>
          </StackView>
        </>
      )}
    </StackView>
  );
};

BrpFileUpload.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    formErrors: PropTypes.objectOf(PropTypes.any).isRequired,
    value: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired
};

export default BrpFileUpload;
